import { HashRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Loader, Stack, VipsoProvider } from '@vipso/shared';
import { store, persistor, type RootState } from './store';
import { AppShellLayout } from './layouts/AppShellLayout';
import { BootPage } from './pages/BootPage';
import { LoginPage } from './pages/LoginPage';
import { OtpPage } from './pages/OtpPage';
import { OrgPickerPage } from './pages/OrgPickerPage';
import { HomePage } from './pages/HomePage';
import { TasksPage } from './pages/TasksPage';
import { CustomersPage } from './pages/CustomersPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { VendorsPage } from './pages/VendorsPage';
import { VendorDetailPage } from './pages/VendorDetailPage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ProductFormPage } from './pages/ProductFormPage';
import { ProcurementPage } from './pages/ProcurementPage';
import { ProcurementDetailPage } from './pages/ProcurementDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { StaffPage } from './pages/StaffPage';
import { WarehousePage } from './pages/WarehousePage';
import { TransportPage } from './pages/TransportPage';
import { LabourPage } from './pages/LabourPage';
import { MobileMorePage } from './pages/MobileMorePage';
import { StorageLocationsPage } from './pages/StorageLocationsPage';
import { InventoryTransferPage } from './pages/InventoryTransferPage';
import { RolesPage } from './pages/RolesPage';
import { ApprovalFlowsPage } from './pages/ApprovalFlowsPage';

function ProtectedRoute() {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const bootstrapped = useSelector((state: RootState) => state.auth.bootstrapped);

  if (!bootstrapped) {
    return <Navigate to="/boot" replace />;
  }

  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/boot" element={<BootPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/otp" element={<OtpPage />} />
        <Route path="/org-picker" element={<OrgPickerPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShellLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<HomePage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/new" element={<ProductFormPage />} />
            <Route path="/products/:id/edit" element={<ProductFormPage />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/orders" element={<ProcurementPage />} />
            <Route path="/orders/:id" element={<ProcurementDetailPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/vendors/:id" element={<VendorDetailPage />} />
            <Route path="/staff" element={<StaffPage />} />
            <Route path="/warehouse" element={<WarehousePage />} />
            <Route path="/storage" element={<StorageLocationsPage />} />
            <Route path="/transport" element={<TransportPage />} />
            <Route path="/labour" element={<LabourPage />} />
            <Route path="/inventory" element={<InventoryTransferPage />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/approval-flows" element={<ApprovalFlowsPage />} />
            <Route path="/more" element={<MobileMorePage />} />
            <Route path="/procurement" element={<Navigate to="/orders" replace />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/boot" replace />} />
      </Routes>
    </HashRouter>
  );
}

function ThemedApp() {
  const theme = useSelector((state: RootState) => state.auth.theme);

  return (
    <VipsoProvider
      themeOverrides={{
        theme,
        primaryColor: theme?.primaryColor || '#cc202e',
        secondaryColor: theme?.secondaryColor || '#ffdbdd',
        fontFamily: theme?.fontFamily || undefined,
      }}
      colorScheme={theme?.mode === 'dark' ? 'dark' : 'light'}
    >
      <PersistGate
        loading={
          <Stack align="center" justify="center" style={{ minHeight: '100vh' }}>
            <Loader />
          </Stack>
        }
        persistor={persistor}
      >
        <AppRoutes />
      </PersistGate>
    </VipsoProvider>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <ThemedApp />
    </Provider>
  );
}
